import {
  type Dispatch,
  type Reducer,
  type ReducerAction,
  type ReducerState,
  useCallback,
  useReducer,
  useRef,
} from 'react';

/**
 * The purpose of this hook is to extend useReducer with a memoized `getState` function that
 * can be used to get the current state of the reducer without having to pass `state` as dependency
 * on other hooks which would invalidate them anytime state was updated. -@erictaylor
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useEnhancedReducer = <R extends Reducer<any, any>, I>(
  reducer: R,
  initializerArgument: I & ReducerState<R>,
): [
  state: ReducerState<R>,
  dispatch: Dispatch<ReducerAction<R>>,
  getState: () => ReducerState<R>,
] => {
  const lastState = useRef(initializerArgument);

  const getState = useCallback(() => lastState.current, []);

  return [
    ...useReducer(
      (state: ReducerState<R>, action: ReducerAction<R>) =>
        (lastState.current = reducer(state, action)),
      initializerArgument,
    ),
    getState,
  ];
};
